const STORAGE_KEY = "gomake.pendingDocumentReportJobs";
const READY_NOTIFICATIONS_KEY = "gomake.documentReportReadyNotifications";

const readPending = (): Set<string> => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

const writePending = (pending: Set<string>) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(pending)));
  } catch {
    // ignore quota / private mode
  }
};

export const trackPendingDocumentReportJob = (jobId: string) => {
  if (!jobId) return;
  const pending = readPending();
  pending.add(jobId);
  writePending(pending);
};

export const clearPendingDocumentReportJob = (jobId: string) => {
  if (!jobId) return;
  const pending = readPending();
  pending.delete(jobId);
  writePending(pending);
};

export const hasPendingDocumentReportJob = (jobId?: string) => {
  if (!jobId) return readPending().size > 0;
  return readPending().has(jobId);
};

export const getPendingDocumentReportJobIds = (): string[] =>
  Array.from(readPending());

export type DocumentReportReadyNotification = {
  id: string;
  type: number;
  message: string;
  url: string;
  createdDate: string;
};

export const getDocumentReportReadyNotifications =
  (): DocumentReportReadyNotification[] => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(READY_NOTIFICATIONS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

export const saveDocumentReportReadyNotification = (
  notification: DocumentReportReadyNotification
) => {
  if (typeof window === "undefined" || !notification?.id) return;
  const existing = getDocumentReportReadyNotifications().filter(
    (n) => n.id !== notification.id
  );
  try {
    sessionStorage.setItem(
      READY_NOTIFICATIONS_KEY,
      JSON.stringify([notification, ...existing])
    );
  } catch {
    // ignore
  }
};

export const removeDocumentReportReadyNotification = (id: string) => {
  if (typeof window === "undefined" || !id) return;
  const next = getDocumentReportReadyNotifications().filter((n) => n.id !== id);
  try {
    sessionStorage.setItem(READY_NOTIFICATIONS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
};

export const clearDocumentReportReadyNotifications = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(READY_NOTIFICATIONS_KEY);
  } catch {
    // ignore
  }
};

export const downloadDocumentReportFile = (
  downloadUrl: string,
  filename = "Reports Rule engine.xlsx"
) => {
  if (!downloadUrl || typeof window === "undefined") return;
  const downloadLink = document.createElement("a");
  downloadLink.href = downloadUrl;
  downloadLink.download = filename;
  downloadLink.target = "_blank";
  downloadLink.rel = "noopener noreferrer";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};
